/**
 * SystemBenchmark — isolierter Read-Only Store inkl. Live-Abruf aktiver Salon-Jahresakten.
 * STRIKT READ-ONLY gegenüber AppStore / Firestore / calculateAllMetrics.
 */
import { create } from 'zustand';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { calculateAllMetrics } from './selectors/calculations';
import { evaluateRentabilitaet } from './utils';
import { defaultClientData } from './slices/coreDataSlice';
import { defaultSettings } from './slices/settingsSlice';
import { isSoloModeActive } from '../utils/soloMode';
import { calculateConvergedUnternehmerStundenlohn } from '../utils/unternehmerlohnConvergence';
import { normalizeLandCode } from './slices/settingsSlice';

export type BenchmarkLandCode = 'DE' | 'AT' | 'CH' | 'IT';

export const LAND_LABEL: Record<BenchmarkLandCode, string> = {
  DE: 'Deutschland',
  AT: 'Österreich',
  CH: 'Schweiz',
  IT: 'Italien / Südtirol',
};

const resolveSalonLand = (source: any, fallback?: BenchmarkLandCode): BenchmarkLandCode =>
  normalizeLandCode(
    source?.Land ||
      source?.land ||
      source?.clientData?.Land ||
      source?.clientData?.land ||
      source?.activeLand ||
      fallback ||
      'DE'
  );

export type BenchmarkUnit = '%' | '€' | 'Faktor' | '€/Std.' | '€/Monat';
export type BenchmarkStatus = 'optimal' | 'warning' | 'critical' | 'neutral';
export type BenchmarkDynamicTone =
  | 'purple'
  | 'emerald'
  | 'lime'
  | 'amber'
  | 'rose'
  | 'cyan'
  | 'teal'
  | 'indigo'
  | 'orange'
  | 'gold'
  | 'slate';
export type BenchmarkTab = 'soll' | 'ist';
export type BenchmarkMode = 'solo' | 'team';

export interface BenchmarkDataPoint {
  metricKey: string;
  label: string;
  sublabel?: string;
  targetHint?: string;
  targetHintTone?: 'orange' | 'cyan' | 'teal' | 'indigo' | 'gold' | 'purple';
  unit: BenchmarkUnit;
  mySalonValue: number;
  peerGroupValue: number | null;
  totalMarketValue: number | null;
  /** Falls im Marktvergleich Empfohlen Ø vom Übernommenen Ø abweicht */
  secondaryMarketNote?: {
    label: string;
    peerValue: number | null;
    marketValue: number | null;
  };
  status: BenchmarkStatus;
  statusLabel?: string;
  statusTone?: BenchmarkDynamicTone;
  statusMessage?: string;
  rowAccentTone?: BenchmarkDynamicTone;
  peerAvailable: boolean;
  peerSampleSize: number;
  marketSampleSize: number;
  section: 'faktoren' | 'kostenquoten' | 'leistung' | 'sollUmsatz' | 'ist';
}

export const TEAM_PEER_MIN_STRICT = 10;
const COHORT_PCT = 0.2;

const n = (v: unknown): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (!trimmed) return 0;
    const normalized =
      trimmed.includes(',') && trimmed.includes('.')
        ? trimmed.replace(/\./g, '').replace(',', '.')
        : trimmed.replace(',', '.');
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const safeDiv = (a: number, b: number): number => {
  if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return 0;
  return a / b;
};

const avg = (vals: number[]): number | null => {
  const clean = vals.filter((v) => Number.isFinite(v) && v > 0);
  if (clean.length === 0) return null;
  return clean.reduce((s, v) => s + v, 0) / clean.length;
};

const avgAllowSigned = (vals: number[]): number | null => {
  const clean = vals.filter((v) => Number.isFinite(v));
  if (clean.length === 0) return null;
  return clean.reduce((s, v) => s + v, 0) / clean.length;
};

/**
 * Ermittelt das aktuell geöffnete Geschäftsjahr des Mandanten (z. B. "2026" oder "2025").
 * Es dürfen im Benchmark NIEMALS verschiedene Geschäftsjahre gemischt werden!
 */
export const resolveActiveBenchmarkYear = (clientData?: any): string => {
  if (typeof window !== 'undefined') {
    const raw = localStorage.getItem('CURRENT_YEAR');
    if (raw && raw !== 'null' && raw !== 'undefined' && raw.trim() !== '') {
      return raw.trim();
    }
  }
  const fromCd = String(
    clientData?.aktivesJahr || clientData?.geschaeftsjahr || ''
  ).trim();
  if (fromCd) return fromCd;
  return String(new Date().getFullYear());
};

/**
 * Prüft streng, ob ein Mandant wirklich AKTIV ist (nicht im Archiv, nicht gelöscht,
 * kein abgelaufenes Kündigungsdatum, kein Vorab-Eintrag ohne Betreuer).
 */
export const isStrictlyActiveSalonRoot = (rootData: any): boolean => {
  if (!rootData || typeof rootData !== 'object') return false;
  const cd = rootData.clientData || {};

  // 1) Explizite Archiv- / Lösch- / Inaktiv-Flags auf Root oder clientData
  if (
    rootData.isArchived === true ||
    cd.isArchived === true ||
    rootData.archived === true ||
    cd.archived === true ||
    rootData.isDeleted === true ||
    cd.isDeleted === true ||
    rootData.isActive === false ||
    cd.isActive === false
  ) {
    return false;
  }

  // 2) Status-Strings prüfen ("archiv", "archived", "gekündigt", "inactive", "deleted")
  const statusStr = String(
    rootData.status || cd.status || rootData.tarifStatus || cd.tarifStatus || ''
  )
    .toLowerCase()
    .trim();
  if (
    statusStr.includes('archiv') ||
    statusStr.includes('kündig') ||
    statusStr.includes('kuendig') ||
    statusStr === 'deleted' ||
    statusStr === 'inactive' ||
    statusStr === 'inaktiv'
  ) {
    return false;
  }

  // 3) Kündigungsdatum in der Vergangenheit -> gehört ins Archiv (wie der Wachhund im AdminDashboard!)
  const cancelDateRaw = rootData.kuendigungsDatum || cd.kuendigungsDatum;
  if (cancelDateRaw) {
    const cancelTime = new Date(cancelDateRaw).getTime();
    if (Number.isFinite(cancelTime) && cancelTime < Date.now()) {
      return false;
    }
  }

  // 4) Muss einem Betreuer/Coach im aktiven CRM zugewiesen sein (keine unzugewiesenen Karteileichen)
  const betreuer = String(
    rootData.betreuer ||
      cd.betreuer ||
      rootData.coachId ||
      cd.coachId ||
      rootData.coach ||
      cd.coach ||
      ''
  ).trim();
  if (!betreuer || betreuer.toLowerCase() === 'archiv') {
    return false;
  }

  return true;
};

interface DynamicEvalResult {
  status: BenchmarkStatus;
  statusLabel: string;
  statusTone: BenchmarkDynamicTone;
  statusMessage?: string;
}

const evalRentabilitaetDynamic = (
  rentabilitaetPct: number,
  gehalt: number,
  ueberschussPct: number
): DynamicEvalResult => {
  // Harte Untergrenzen laut System-Vorgabe: < 15 % Kritisch, < 30 % Grenzwertig, >= 35 % Ziel
  const doppelSchloss = evaluateRentabilitaet(gehalt, ueberschussPct);

  if (
    !Number.isFinite(rentabilitaetPct) ||
    rentabilitaetPct < 15 ||
    doppelSchloss.status === 'Kritisch'
  ) {
    return {
      status: 'critical',
      statusLabel: 'Kritisch',
      statusTone: 'rose',
      statusMessage:
        rentabilitaetPct < 15
          ? 'Unter 15 % Rentabilität ist kritisch — Inhaber-Ertrag und Rücklage reichen nicht.'
          : doppelSchloss.message,
    };
  }

  if (rentabilitaetPct < 30 || doppelSchloss.status === 'Grenzwertig') {
    return {
      status: 'warning',
      statusLabel: 'Grenzwertig',
      statusTone: 'amber',
      statusMessage:
        rentabilitaetPct < 30
          ? 'Unter 30 % ist grenzwertig — Ziel liegt über 35 % für langfristige Sicherheit.'
          : doppelSchloss.message,
    };
  }

  if (rentabilitaetPct < 35 || doppelSchloss.status === 'Solide') {
    return {
      status: 'optimal',
      statusLabel: 'Solide',
      statusTone: 'lime',
      statusMessage: doppelSchloss.message,
    };
  }

  if (rentabilitaetPct >= 42 && doppelSchloss.status === 'Exzellent') {
    return {
      status: 'optimal',
      statusLabel: 'Exzellent',
      statusTone: 'purple',
      statusMessage: doppelSchloss.message,
    };
  }

  if (doppelSchloss.status === 'Exzellent') {
    return {
      status: 'optimal',
      statusLabel: 'Exzellent',
      statusTone: 'purple',
      statusMessage: doppelSchloss.message,
    };
  }

  return {
    status: 'optimal',
    statusLabel: 'Gut',
    statusTone: 'emerald',
    statusMessage: doppelSchloss.message,
  };
};

/**
 * Bewertet die Kennzahl anhand der offiziellen PreisExpert-Dynamiken & BWA-Planziele
 * inkl. der Original-Farbcodes aus Kostenstruktur / Gesamtleistung / HolyGrail.
 */
const evaluateBySystemTarget = (
  metricKey: string,
  my: number,
  ownValues: Record<string, number>,
  mode: BenchmarkMode = 'team'
): DynamicEvalResult => {
  if (!Number.isFinite(my)) {
    return { status: 'neutral', statusLabel: 'Referenz', statusTone: 'slate' };
  }

  switch (metricKey) {
    case 'gemeinkostenfaktor':
      // Solo: selten unter 40 %, meistens über 40 % — Ziel unter 50 % bleiben!
      if (mode === 'solo') {
        if (my < 40.0)
          return {
            status: 'optimal',
            statusLabel: 'Exzellent (< 40 %)',
            statusTone: 'purple',
          };
        if (my <= 45.0)
          return {
            status: 'optimal',
            statusLabel: 'Gut (≤ 45 %)',
            statusTone: 'emerald',
          };
        if (my < 50.0)
          return {
            status: 'optimal',
            statusLabel: 'Solide (< 50 %)',
            statusTone: 'lime',
          };
        if (my <= 53.0)
          return {
            status: 'warning',
            statusLabel: 'Knapp (um 50 %)',
            statusTone: 'amber',
          };
        return {
          status: 'critical',
          statusLabel: 'Kritisch (≥ 50 %)',
          statusTone: 'rose',
        };
      }
      // Team: unbedingt unter 40 %, Ziel möglichst ~30 %
      if (my <= 30.5)
        return {
          status: 'optimal',
          statusLabel: 'Exzellent (≤ 30 %)',
          statusTone: 'purple',
        };
      if (my <= 35.0)
        return {
          status: 'optimal',
          statusLabel: 'Gut',
          statusTone: 'emerald',
        };
      if (my < 40.0)
        return {
          status: 'optimal',
          statusLabel: 'Solide (< 40 %)',
          statusTone: 'lime',
        };
      if (my <= 42.0)
        return {
          status: 'warning',
          statusLabel: 'Grenzwertig',
          statusTone: 'amber',
        };
      return {
        status: 'critical',
        statusLabel: 'Kritisch (≥ 40 %)',
        statusTone: 'rose',
      };

    case 'prodLohnkostenfaktor':
      // Gesunde Lohnquote: 40–45 % — darunter oder darüber mit Vorsicht zu genießen!
      if (my >= 40 && my <= 45)
        return {
          status: 'optimal',
          statusLabel: 'Gesund (40–45 %)',
          statusTone: 'emerald',
        };
      if ((my >= 36 && my < 40) || (my > 45 && my <= 48))
        return {
          status: 'warning',
          statusLabel: 'Vorsicht',
          statusTone: 'amber',
        };
      return {
        status: 'critical',
        statusLabel: my < 36 ? 'Zu niedrig (< 40 %)' : 'Zu hoch (> 45 %)',
        statusTone: 'rose',
      };

    case 'lohnfaktorAktiv': {
      // Hängt vom Preisniveau ab — prüfen, ob realer Stundensatz den Soll-Stundenumsatz trägt.
      // (z. B. 80 € Satz bei 88 € Soll = ~91 % -> Gelb "Preise knapp", nicht Rot!)
      const satzReell = n(ownValues.stundensatzOhneMatReell);
      const soll75 = n(ownValues.sollUmsatz75);
      if (satzReell > 0 && soll75 > 0) {
        if (satzReell >= soll75) {
          return {
            status: 'optimal',
            statusLabel: 'Preise tragen Faktor',
            statusTone: 'emerald',
          };
        }
        if (satzReell >= soll75 * 0.85) {
          return {
            status: 'warning',
            statusLabel: 'Preise knapp',
            statusTone: 'amber',
          };
        }
        return {
          status: 'critical',
          statusLabel: 'Preise deutlich zu niedrig',
          statusTone: 'rose',
        };
      }
      return {
        status: 'neutral',
        statusLabel: 'Preisabhängig',
        statusTone: 'gold',
      };
    }

    case 'rentabilitaetSoll':
      return evalRentabilitaetDynamic(
        my,
        n(ownValues.unternehmerlohn),
        n(ownValues.ueberschussSollProzent)
      );

    case 'rentabilitaetIst':
      return evalRentabilitaetDynamic(
        my,
        n(ownValues.unternehmerlohn),
        n(ownValues.bwaErgebnisProzent)
      );

    case 'materialkostenquote':
      // BWA-Planziel: 8–10 %
      if (my >= 7.5 && my <= 10.5)
        return {
          status: 'optimal',
          statusLabel: 'Im Planziel (8–10 %)',
          statusTone: 'emerald',
        };
      if (my < 7.5)
        return {
          status: 'optimal',
          statusLabel: 'Sehr niedrig (< 8 %)',
          statusTone: 'lime',
        };
      if (my <= 12.5)
        return {
          status: 'warning',
          statusLabel: 'Erhöht',
          statusTone: 'amber',
        };
      return {
        status: 'critical',
        statusLabel: 'Über Planziel',
        statusTone: 'rose',
      };

    case 'raumkostenquote':
      // BWA-Planziel: 10–12 %
      if (my <= 10.0)
        return {
          status: 'optimal',
          statusLabel: 'Exzellent (≤ 10 %)',
          statusTone: 'emerald',
        };
      if (my <= 12.0)
        return {
          status: 'optimal',
          statusLabel: 'Im Planziel (10–12 %)',
          statusTone: 'lime',
        };
      if (my <= 14.5)
        return {
          status: 'warning',
          statusLabel: 'Grenzwertig',
          statusTone: 'amber',
        };
      return {
        status: 'critical',
        statusLabel: 'Zu hoch (> 12 %)',
        statusTone: 'rose',
      };

    case 'werbekostenquote':
      // BWA-Planziel: 2–5 %
      if (my >= 1.5 && my <= 5.0)
        return {
          status: 'optimal',
          statusLabel: 'Im Planziel (2–5 %)',
          statusTone: 'emerald',
        };
      if (my < 1.5)
        return {
          status: 'warning',
          statusLabel: 'Kaum Sichtbarkeit',
          statusTone: 'amber',
        };
      if (my <= 6.5)
        return {
          status: 'warning',
          statusLabel: 'Erhöht',
          statusTone: 'amber',
        };
      return {
        status: 'critical',
        statusLabel: 'Über Planziel (> 5 %)',
        statusTone: 'rose',
      };

    case 'sonstigeKostenquote':
      // BWA-Planziel: ca. 10–12 %
      if (my <= 10.0)
        return {
          status: 'optimal',
          statusLabel: 'Top (≤ 10 %)',
          statusTone: 'emerald',
        };
      if (my <= 12.0)
        return {
          status: 'optimal',
          statusLabel: 'Im Planziel (10–12 %)',
          statusTone: 'lime',
        };
      if (my <= 14.0)
        return {
          status: 'warning',
          statusLabel: 'Grenzwertig',
          statusTone: 'amber',
        };
      return {
        status: 'critical',
        statusLabel: 'Zu hoch (> 12 %)',
        statusTone: 'rose',
      };

    case 'stundensatzOhneMatReell': {
      const empf = n(ownValues.stundensatzOhneMatEmpfohlen);
      if (empf <= 0)
        return { status: 'neutral', statusLabel: 'Referenz', statusTone: 'slate' };
      if (my > empf * 1.05)
        return { status: 'optimal', statusLabel: 'Exzellent', statusTone: 'purple' };
      if (my >= empf)
        return { status: 'optimal', statusLabel: 'Empfehlung gedeckt', statusTone: 'emerald' };
      if (my >= empf * 0.92)
        return { status: 'warning', statusLabel: 'Knapp unter Empf.', statusTone: 'amber' };
      return { status: 'critical', statusLabel: 'Unterdeckung', statusTone: 'rose' };
    }

    case 'stundensatzInklMatReell': {
      const empf = n(ownValues.stundensatzInklMatEmpfohlen);
      if (empf <= 0)
        return { status: 'neutral', statusLabel: 'Referenz', statusTone: 'slate' };
      if (my > empf * 1.05)
        return { status: 'optimal', statusLabel: 'Exzellent', statusTone: 'purple' };
      if (my >= empf)
        return { status: 'optimal', statusLabel: 'Empfehlung gedeckt', statusTone: 'emerald' };
      if (my >= empf * 0.92)
        return { status: 'warning', statusLabel: 'Knapp unter Empf.', statusTone: 'amber' };
      return { status: 'critical', statusLabel: 'Unterdeckung', statusTone: 'rose' };
    }

    case 'sollUmsatz75': {
      const satzReell = n(ownValues.stundensatzOhneMatReell);
      if (satzReell > 0 && my > 0) {
        if (satzReell >= my * 1.1)
          return { status: 'optimal', statusLabel: 'Stark gedeckt', statusTone: 'purple' };
        if (satzReell >= my)
          return { status: 'optimal', statusLabel: 'Durch Satz gedeckt', statusTone: 'emerald' };
        if (satzReell >= my * 0.85)
          return { status: 'warning', statusLabel: 'Preise knapp', statusTone: 'amber' };
        return { status: 'critical', statusLabel: 'Satz deutlich zu niedrig', statusTone: 'rose' };
      }
      return { status: 'neutral', statusLabel: 'Referenz', statusTone: 'slate' };
    }

    case 'verkaufsfaktor':
      // Wie HolyGrailDashboard: >=15 Exzellent, >=10 Sehr Gut, >=7 Gut, >=5 Akzeptabel
      if (my >= 15)
        return { status: 'optimal', statusLabel: 'Exzellent', statusTone: 'purple' };
      if (my >= 10)
        return { status: 'optimal', statusLabel: 'Sehr Gut (≥ 10 %)', statusTone: 'emerald' };
      if (my >= 7)
        return { status: 'optimal', statusLabel: 'Solide', statusTone: 'lime' };
      if (my >= 5)
        return { status: 'warning', statusLabel: 'Ausbaufähig', statusTone: 'amber' };
      return { status: 'critical', statusLabel: 'Kritisch (< 5 %)', statusTone: 'rose' };

    case 'umsatzauslastung':
      // Wie HolyGrailDashboard: >=85 Exzellent, >=75 Sehr Gut, >=70 Gut, >=60 Akzeptabel
      if (my >= 85)
        return { status: 'optimal', statusLabel: 'Exzellent', statusTone: 'purple' };
      if (my >= 75)
        return { status: 'optimal', statusLabel: 'Sehr Gut', statusTone: 'emerald' };
      if (my >= 70)
        return { status: 'optimal', statusLabel: 'Im Ziel (70–75 %)', statusTone: 'lime' };
      if (my >= 60)
        return { status: 'warning', statusLabel: 'Grenzwertig', statusTone: 'amber' };
      return { status: 'critical', statusLabel: 'Kritisch (< 60 %)', statusTone: 'rose' };

    case 'bwaErgebnisProzent':
      // Wie Kostenstruktur: >= 10 % Ziel erreicht, > 0 Kostendeckend, <= 0 Verlust
      if (my >= 15)
        return { status: 'optimal', statusLabel: 'Exzellent', statusTone: 'purple' };
      if (my >= 10)
        return { status: 'optimal', statusLabel: 'Ziel erreicht (≥ 10 %)', statusTone: 'emerald' };
      if (my >= 5)
        return { status: 'optimal', statusLabel: 'Solide', statusTone: 'lime' };
      if (my > 0)
        return { status: 'warning', statusLabel: 'Kostendeckend', statusTone: 'amber' };
      return { status: 'critical', statusLabel: 'Verlust / Unterdeckung', statusTone: 'rose' };

    default:
      return { status: 'neutral', statusLabel: 'Referenz', statusTone: 'slate' };
  }
};

export const computeMetricsWithAutoConvergence = (
  rawClientData: any,
  settings: any
): { calc: any; clientData: any } => {
  const safeSettings = settings || defaultSettings;
  const cd = { ...defaultClientData, ...(rawClientData || {}) };
  let calc = calculateAllMetrics(cd as any, safeSettings as any);

  const lfData = cd.lohnfaktorData || {};
  const isManualStundenlohnActive = lfData.isManualStundenlohnActive === true;
  const hours = n(calc?.monatsstundenChef);

  if (!isManualStundenlohnActive && cd.unternehmensform !== 'GmbH' && hours > 0) {
    const bedarf = n(calc.echterBedarfGesamt ?? calc.gesamtlohn);
    const prodUL = n(calc.prodUnternehmerlohn);
    const unprodUL = n(calc.unprodUnternehmerlohn);
    const sollumsatzOhneUnternehmerlohn = n(calc.sollumsatz) - prodUL - unprodUL;
    const ohneAzubis =
      n(calc.produktiveLohnkostenMitarbeiterOhneAzubis) ||
      Math.max(0, n(calc.produktiveKostenTabelle) - prodUL);
    const lohnfaktorNennerMitarbeiterOhneAzubis = Math.max(
      0,
      n(calc.produktiveKostenTabelle) - prodUL
    );
    const isSolo =
      isSoloModeActive(cd) || Number(calc.personalData?.length || 0) === 0;
    const sicherheitsfaktorPercent =
      lfData.sicherheitsfaktor === null ||
      lfData.sicherheitsfaktor === undefined ||
      lfData.sicherheitsfaktor === ''
        ? 25
        : n(lfData.sicherheitsfaktor);

    const ideal = calculateConvergedUnternehmerStundenlohn({
      isSolo,
      echterBedarfGesamt: bedarf,
      chefMonatsstunden: hours,
      sollumsatzOhneUnternehmerlohn,
      produktiveLohnkostenMitarbeiterOhneAzubis: ohneAzubis,
      lohnfaktorNennerMitarbeiterOhneAzubis,
      kalkulierterUnternehmerUmsatzBrutto: n(calc.kalkulierterUnternehmerUmsatzBrutto),
      mwstSatz: n(calc.mwstSatz),
      sicherheitsfaktorPercent,
      urlaubsausgleichPercent: n(calc.lohnfortzahlungPercent) || 20,
      initialGuess:
        n(calc.realisierterStundenlohnChef) ||
        n(lfData.unternehmerStundenlohn_editable) ||
        0,
    });

    const currentSoll = n(lfData.unternehmerStundenlohn_editable);
    if (ideal > 0 && Math.abs(currentSoll - ideal) >= 0.005) {
      const patchedCd = {
        ...cd,
        lohnfaktorData: {
          ...lfData,
          unternehmerStundenlohn_editable: ideal,
        },
      };
      calc = calculateAllMetrics(patchedCd as any, safeSettings as any);
      return { calc, clientData: patchedCd };
    }
  }

  return { calc, clientData: cd };
};

/**
 * EXAKTE Stundensatz-Auflösung wie in HolyGrailDashboard.tsx (Zeilen 737-758) & KalkulatorPage.tsx (Zeilen 843-858):
 * - Empfohlen (ohne Mat) = `barometerStundenfaktor` (bei Susi = 80 €!)
 * - Übernommen / Reell (ohne Mat) = NUR wenn `useEchterStundensatz === true && echterStundensatz > 0`, sonst = `barometerStundenfaktor` (80 €)!
 * - Empfohlen (inkl. Mat) = `baseMatParam.preis` (bei Susi = 130 €!)
 * - Übernommen / Reell (inkl. Mat) = NUR wenn `useEchterStundensatzMat === true && echterStundensatzMat > 0`, sonst = `targetMatPreis` (130 €)!
 * KEIN Fallback auf irgendwelche 75 € Mitarbeiter-Soll-Sätze!
 */
export const resolveHolyGrailHourRatesDetailed = (
  clientData: any
): {
  ohneMaterialEmpfohlen: number;
  ohneMaterialReell: number;
  inklMaterialEmpfohlen: number;
  inklMaterialReell: number;
} => {
  const kalk = clientData?.kalkulator || {};
  const stundenfaktor = n(kalk.barometerStundenfaktor);
  const echter = n(kalk.echterStundensatz);
  const useEchter = kalk.useEchterStundensatz === true;

  const ohneMaterialEmpfohlen = Math.round(stundenfaktor);
  const ohneMaterialReell = Math.round(
    useEchter && echter > 0 ? echter : stundenfaktor
  );

  const clientParams = Array.isArray(kalk.priceParameterListe)
    ? kalk.priceParameterListe
    : [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const baseMat =
    clientParams.length > 0
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        clientParams.find(
          (p: any) =>
            String(p?.desc || '')
              .toLowerCase()
              .includes('stundensatz') ||
            String(p?.desc || '')
              .toLowerCase()
              .includes('verrechnungssatz')
        ) ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        clientParams.reduce(
          (max: any, p: any) => (n(p?.preis) > n(max?.preis) ? p : max),
          clientParams[0]
        )
      : null;

  const targetMat = baseMat ? n(baseMat.preis) : 0;
  const echterMat = n(kalk.echterStundensatzMat);
  const useEchterMat = kalk.useEchterStundensatzMat === true;

  const inklMaterialEmpfohlen = Math.round(targetMat);
  const inklMaterialReell = Math.round(
    useEchterMat && echterMat > 0 ? echterMat : targetMat
  );

  return {
    ohneMaterialEmpfohlen,
    ohneMaterialReell,
    inklMaterialEmpfohlen,
    inklMaterialReell,
  };
};

export interface ExtractedSalonMetrics {
  uid?: string;
  year?: string;
  mandantNr?: string;
  firmenname?: string;
  /** Land des Salons — Standard-Vergleich nur innerhalb desselben Landes */
  land: BenchmarkLandCode;
  mode: BenchmarkMode;
  monatsumsatzNetto: number;
  auslastungPct: number;
  isManualLfHigher: boolean;
  isPlausibleForMarket: boolean;
  values: Record<string, number>;
}

export const extractSalonBenchmarkMetrics = (
  calc: any,
  clientData: any,
  uid?: string,
  year?: string
): ExtractedSalonMetrics => {
  const solo = isSoloModeActive(clientData);
  const mode: BenchmarkMode = solo ? 'solo' : 'team';
  const monatsumsatzNetto = n(
    calc?.aggregatedMonatsumsatzNetto ?? calc?.monatsumsatzNetto
  );
  const auslastungPct =
    n(calc?.auslastungsfaktor) ||
    n(calc?.settings?.global?.Auslastungsfaktor) ||
    75;
  const wochensatz =
    n(clientData?.wochensatz) || n(calc?.settings?.global?.Wochensatz) || 4.33;

  // —— Lohnfaktor: aktiv vs. manuell eingegebener Wert ——
  const lfFortzahlung = n(calc?.lohnfaktorFortzahlung);
  const lfAktivStore = n(calc?.lohnfaktorAktiv);
  const lfManual = n(
    clientData?.lohnfaktorData?.manualLohnfaktor ??
      calc?.lohnfaktorData?.manualLohnfaktor
  );
  const effectiveLohnfaktor = Math.max(
    3.5,
    lfFortzahlung,
    lfAktivStore,
    lfManual
  );
  const isManualLfHigher = lfManual > 0 && lfManual > lfFortzahlung;

  // —— Produktive Mitarbeiter filtern ——
  const personalRaw = Array.isArray(calc?.personalData)
    ? calc.personalData
    : Array.isArray(clientData?.personalData)
      ? clientData.personalData
      : [];

  const produktiv = personalRaw.filter((ma: any) => {
    const kat = String(ma?.kategorie || 'produktiv').toLowerCase();
    if (kat === 'unproduktiv' || kat === 'azubi') return false;
    const taet = String(ma?.taetigkeit || '');
    if (['Azubi', 'Prod. Azubi', 'produktiver Azubi'].includes(taet)) {
      return false;
    }
    return true;
  });

  // Grundlohn Ø (reines Grundgehalt) vs. Gesamtlohn Ø (inkl. Zusatz & Bonus, ohne AG-NK)
  const grundlohnProdAvg =
    avg(
      produktiv.map((ma: any) => {
        const ms = n(ma?.monatsstunden) || n(ma?.wochenstunden) * wochensatz;
        const reinesGrundgehalt = n(
          ma?.grundgehalt ?? ma?.grossWage ?? ma?.netWage
        );
        if (ms > 0 && reinesGrundgehalt > 0) {
          return reinesGrundgehalt / ms;
        }
        return n(ma?.stundenlohnGrund);
      })
    ) ?? 0;

  const gesamtlohnProdAvg =
    avg(
      produktiv.map((ma: any) => {
        const ms = n(ma?.monatsstunden) || n(ma?.wochenstunden) * wochensatz;
        const reinesGrundgehalt = n(
          ma?.grundgehalt ?? ma?.grossWage ?? ma?.netWage
        );
        const zusatz = n(ma?.lohnkostenzusatz ?? ma?.lohnzusatz);
        const bonus = n(ma?.bonus);
        const gesamtBruttoOhneAgNk = reinesGrundgehalt + zusatz + bonus;
        if (ms > 0 && gesamtBruttoOhneAgNk > 0) {
          return gesamtBruttoOhneAgNk / ms;
        }
        return n(ma?.stundenlohnGesamt);
      })
    ) ?? 0;

  // —— Soll / Std. (100 %) & Soll bei 75 % als Ø über alle produktiven Mitarbeiter ——
  const geraeteListe = Array.isArray(calc?.geraeteListe)
    ? calc.geraeteListe
    : Array.isArray(clientData?.geraeteListe)
      ? clientData.geraeteListe
      : [];

  const sollStd100List: number[] = [];
  const sollStd75List: number[] = [];

  produktiv.forEach((ma: any) => {
    const monatsStunden =
      n(ma?.monatsstunden) || n(ma?.wochenstunden) * wochensatz;
    if (monatsStunden <= 0) return;

    const grundgehalt = n(ma?.grundgehalt ?? ma?.grossWage ?? ma?.netWage);
    const lohnzusatz = n(ma?.lohnkostenzusatz ?? ma?.lohnzusatz);
    const effektiverBruttolohn =
      n(ma?.grundlohn) > 0 ? n(ma?.grundlohn) : grundgehalt + lohnzusatz;

    let maLf = effectiveLohnfaktor;
    const rawLeistung = ma?.id ? clientData?.mitarbeiterLeistung?.[ma.id] : null;
    const selectedDeviceIds = Array.isArray(rawLeistung?.selectedDeviceIds)
      ? rawLeistung.selectedDeviceIds
      : Array.isArray(ma?.selectedDeviceIds)
        ? ma.selectedDeviceIds
        : [];
    if (
      (ma?.arbeitetMitGeraeten || ma?.hasGeraete) &&
      selectedDeviceIds.length > 0
    ) {
      let totalGeraeteProzent = 0;
      selectedDeviceIds.forEach((devId: string) => {
        const g = geraeteListe.find((item: any) => item && item.id === devId);
        if (g) totalGeraeteProzent += n(g.kostenProzent);
      });
      maLf += (totalGeraeteProzent / 100) * 10;
    }

    const maSollMonat = effektiverBruttolohn * maLf;
    const maSollStd100 = maSollMonat / monatsStunden;
    const maSollStd75 =
      auslastungPct > 0
        ? maSollStd100 / (auslastungPct / 100)
        : maSollStd100 / 0.75;

    if (maSollStd100 > 0) {
      sollStd100List.push(maSollStd100);
      sollStd75List.push(maSollStd75);
    }
  });

  let avgSollProStunde100 = avg(sollStd100List) ?? 0;
  let avgSollProStunde75 = avg(sollStd75List) ?? 0;

  if (avgSollProStunde100 <= 0) {
    const chefStd100 = n(calc?.bruttoumsatzProStunde);
    if (chefStd100 > 0) {
      avgSollProStunde100 = chefStd100;
      avgSollProStunde75 =
        auslastungPct > 0
          ? chefStd100 / (auslastungPct / 100)
          : chefStd100 / 0.75;
    }
  }

  // Stundensätze exakt aus dem Kalkulator (ohne 75-€-Fallback!)
  const hourRates = resolveHolyGrailHourRatesDetailed(clientData);

  // —— Kalkulatorischer Unternehmerlohn = Entnahme + Sozialkosten (z. B. 3.800 €) ——
  const calcGesamtlohn = n(calc?.gesamtlohn ?? calc?.echterBedarfGesamt);
  const entnahmePlusSozial =
    n(clientData?.unternehmerData?.entnahme) +
    n(clientData?.unternehmerData?.sozialkosten);
  const unternehmerlohn =
    calcGesamtlohn > 0 ? calcGesamtlohn : entnahmePlusSozial;

  const bwa = clientData?.bwaData || calc?.bwaData || {};
  const zeitraum = Math.max(1, n(bwa.zeitraum) || 12);
  const quoteFromBwa = (key: string): number => {
    if (monatsumsatzNetto <= 0) return 0;
    const yearly = n(bwa[key]);
    const monthly = yearly / zeitraum;
    return (monthly / monatsumsatzNetto) * 100;
  };

  const gemeinkostenPct = n(calc?.gemeinkostenfaktor) * 100;
  const prodLohnPct = n(calc?.prodLohnkostenfaktor) * 100;
  const rentabilitaetSollPct = n(calc?.rentabilitaetSoll);
  const ueberschussSollProzent = n(calc?.ueberschussProzent);
  const materialPct = n(calc?.materialkostenfaktor) * 100;

  // =========================================================================
  // TAB 2 (IST): EXAKTE BERECHNUNG SYNCHRON ZU GesamtleistungSeite.tsx!
  // =========================================================================
  const mwstSatz = n(calc?.mwstSatz);
  const mwstFaktor = clientData?.isVatExempt ? 1 : 1 + (mwstSatz || 19) / 100;
  const ruecklagenZielFaktor =
    n(calc?.settings?.global?.Liquiditaet) / 100;

  const baseMindestumsatzNetto = n(calc?.sollumsatz);
  const mindestumsatz1 =
    n(clientData?.lohnfaktorData?.mindestumsatz1) || baseMindestumsatzNetto;
  const mindestumsatz2 =
    n(clientData?.lohnfaktorData?.mindestumsatz2) || baseMindestumsatzNetto;

  const geoeffneteStundenArr = Array.isArray(
    clientData?.unternehmerData?.geoeffneteStunden
  )
    ? clientData.unternehmerData.geoeffneteStunden
    : [];
  const oeffnungsWochenstunden = geoeffneteStundenArr.reduce(
    (s: number, h: any) => s + n(h),
    0
  );
  const salonMonatsStunden =
    oeffnungsWochenstunden > 0
      ? oeffnungsWochenstunden * wochensatz
      : n(calc?.summeStundenTeam) || 160;

  const glRaw = Array.isArray(clientData?.gesamtleistungData)
    ? clientData.gesamtleistungData
    : [];

  let activeMonthsGL = 0;
  let sumBedienBrutto = 0;
  let sumVkBrutto = 0;
  let sumGesamtBrutto = 0;
  let sumProzentGL = 0;
  let sumMonatsstundenGL = 0;

  for (let i = 0; i < 12; i++) {
    const monat = glRaw[i] || {};
    const bedien = n(monat.bedienumsatzBrutto ?? monat.bedienUmsatz);
    const vk = n(monat.vkUmsatzBrutto ?? monat.vkUmsatz);
    const gesamtBrutto = bedien + vk;
    const isLocked = monat.abgeschlossen === true;
    const isActive = gesamtBrutto > 0 || isLocked;
    if (!isActive) continue;

    const snap = monat.frozenCalculations || {};
    const currentBaseMindest = i < 6 ? mindestumsatz1 : mindestumsatz2;
    const activeMindestBase =
      isLocked && snap.mindestumsatzBase !== undefined
        ? n(snap.mindestumsatzBase)
        : currentBaseMindest;
    const activeMwst =
      isLocked && snap.mwstFaktor !== undefined
        ? n(snap.mwstFaktor)
        : mwstFaktor;
    const activeMs =
      isLocked && snap.monatsstunden !== undefined
        ? n(snap.monatsstunden)
        : salonMonatsStunden;

    const anpassung = monat.kostenErhoeht ? -1 : 1;
    const angepassterMindest =
      activeMindestBase + n(monat.aussergewoehnlich) * anpassung;

    if (gesamtBrutto > 0) {
      const gesamtNetto = safeDiv(gesamtBrutto, activeMwst);
      const ruecklagenDelta = gesamtNetto - angepassterMindest;
      const prozentMonat = safeDiv(ruecklagenDelta, angepassterMindest) * 100;
      sumProzentGL += prozentMonat;
    }

    activeMonthsGL += 1;
    sumBedienBrutto += bedien;
    sumVkBrutto += vk;
    sumGesamtBrutto += gesamtBrutto;
    sumMonatsstundenGL += activeMs || salonMonatsStunden;
  }

  const bwaErgebnisProzent =
    activeMonthsGL > 0
      ? sumProzentGL / activeMonthsGL
      : n(calc?.ueberschussProzent);

  const verkaufsfaktor =
    sumBedienBrutto > 0
      ? safeDiv(sumVkBrutto, sumBedienBrutto) * 100
      : n(calc?.unternehmerMetrics?.vkFaktor) * 100;

  let umsatzauslastung = 0;
  if (activeMonthsGL > 0 && baseMindestumsatzNetto > 0 && salonMonatsStunden > 0) {
    const avgStdUmsatz = safeDiv(
      sumGesamtBrutto,
      sumMonatsstundenGL || salonMonatsStunden * activeMonthsGL
    );
    const korrekterSollStdBrutto =
      safeDiv(
        baseMindestumsatzNetto * (1 + ruecklagenZielFaktor),
        salonMonatsStunden
      ) * mwstFaktor;
      umsatzauslastung =
      (auslastungPct / 100) *
      safeDiv(avgStdUmsatz, korrekterSollStdBrutto) *
      100;
  }

  // Reale (aktive) Rentabilität (IST) — synchron zu GesamtleistungSeite & calculations.ts
  const rentabilitaetIstRaw = n(calc?.rentabilitaetIst);
  const rentabilitaetIstPct =
    activeMonthsGL > 0
      ? rentabilitaetIstRaw > 0 && rentabilitaetIstRaw <= 2
        ? rentabilitaetIstRaw * 100
        : rentabilitaetIstRaw
      : rentabilitaetSollPct;

  const isPlausibleForMarket =
    monatsumsatzNetto >= 1000 &&
    n(calc?.sollumsatz) >= 1000 &&
    gemeinkostenPct > 2 &&
    gemeinkostenPct < 85 &&
    rentabilitaetSollPct >= -30 &&
    rentabilitaetSollPct <= 70;

  return {
    uid,
    year,
    mandantNr: String(clientData?.mandantNr || ''),
    firmenname: String(
      clientData?.firmenname || clientData?.salonname || 'Salon'
    ),
    land: resolveSalonLand(clientData, resolveSalonLand(calc)),
    mode,
    monatsumsatzNetto,
    auslastungPct,
    isManualLfHigher,
    isPlausibleForMarket,
    values: {
      gemeinkostenfaktor: gemeinkostenPct,
      prodLohnkostenfaktor: prodLohnPct,
      lohnfaktorAktiv: effectiveLohnfaktor,
      rentabilitaetSoll: rentabilitaetSollPct,
      rentabilitaetIst: rentabilitaetIstPct,
      ueberschussSollProzent,
      materialkostenquote: materialPct,
      raumkostenquote: quoteFromBwa('raumkosten'),
      werbekostenquote: quoteFromBwa('werbeReisekosten'),
      sonstigeKostenquote: quoteFromBwa('sonstigeKosten'),
      grundlohnProdAvg,
      gesamtlohnProdAvg,
      stundensatzOhneMatEmpfohlen: hourRates.ohneMaterialEmpfohlen,
      stundensatzOhneMatReell: hourRates.ohneMaterialReell,
      stundensatzInklMatEmpfohlen: hourRates.inklMaterialEmpfohlen,
      stundensatzInklMatReell: hourRates.inklMaterialReell,
      sollUmsatz100: avgSollProStunde100,
      sollUmsatz75: avgSollProStunde75,
      unternehmerlohn,
      verkaufsfaktor,
      umsatzauslastung,
      bwaErgebnisProzent,
    },
  };
};

const ALL_METRIC_KEYS = [
  'gemeinkostenfaktor',
  'prodLohnkostenfaktor',
  'lohnfaktorAktiv',
  'rentabilitaetSoll',
  'rentabilitaetIst',
  'ueberschussSollProzent',
  'materialkostenquote',
  'raumkostenquote',
  'werbekostenquote',
  'sonstigeKostenquote',
  'grundlohnProdAvg',
  'gesamtlohnProdAvg',
  'stundensatzOhneMatEmpfohlen',
  'stundensatzOhneMatReell',
  'stundensatzInklMatEmpfohlen',
  'stundensatzInklMatReell',
  'sollUmsatz100',
  'sollUmsatz75',
  'unternehmerlohn',
  'verkaufsfaktor',
  'umsatzauslastung',
  'bwaErgebnisProzent',
];

const cachedByYear: Record<string, { docs: any[]; at: number }> = {};
const CACHE_TTL_MS = 1000 * 60 * 5;

/**
 * Lädt ausschließlich:
 * 1) NICHT-archivierte, aktive Salons (`isStrictlyActiveSalonRoot`)
 * 2) AUSSCHLIESSLICH das identische Geschäftsjahr (`targetYear`), das gerade geöffnet ist!
 *    Hat ein Salon für dieses Geschäftsjahr keine Jahresakte, wird er NICHT aus einem anderen Jahr gemischt!
 */
export const fetchHydratedActiveSalonsForYearReadOnly = async (
  targetYear: string
): Promise<any[]> => {
  const cleanYear = String(targetYear || new Date().getFullYear()).trim();
  const cached = cachedByYear[cleanYear];
  if (cached && Date.now() - cached.at < CACHE_TTL_MS && cached.docs.length > 0) {
    return cached.docs;
  }

  try {
    const snap = await getDocs(collection(db, 'kalkulation_mandanten'));
    const activeRoots: Array<{ uid: string; rootData: any }> = [];

    snap.forEach((docSnap) => {
      const uid = docSnap.id;
      if (uid === 'system_data' || uid === 'master') return;
      const d = docSnap.data() || {};
      if (!isStrictlyActiveSalonRoot(d)) return;
      activeRoots.push({ uid, rootData: d });
    });

    const hydratedResults = await Promise.all(
      activeRoots.map(async ({ uid, rootData }) => {
        try {
          // STRIKT: Nur das angeforderte Geschäftsjahr `cleanYear` prüfen!
          const yearRef = doc(
            db,
            'kalkulation_mandanten',
            uid,
            'jahre',
            cleanYear
          );
          const yearSnap = await getDoc(yearRef);
          if (!yearSnap.exists()) {
            // Salon hat für dieses Geschäftsjahr keine Akte -> NICHT aus anderem Jahr mischen!
            return null;
          }
          const yData = yearSnap.data() || {};
          // Auch auf Jahres-Ebene prüfen, ob dort archiviert wurde
          if (yData.isArchived === true || yData.clientData?.isArchived === true) {
            return null;
          }

          const mergedCd = {
            ...defaultClientData,
            ...(rootData.clientData || {}),
            ...(yData.clientData || {}),
            firmenname:
              yData.clientData?.firmenname ||
              rootData.firmenname ||
              rootData.clientData?.firmenname ||
              rootData.salonname ||
              '',
            mandantNr:
              yData.clientData?.mandantNr ||
              rootData.mandantNr ||
              rootData.clientData?.mandantNr ||
              '',
            isSolo:
              rootData.isSolo ??
              yData.clientData?.isSolo ??
              rootData.clientData?.isSolo,
            forceSoloTarif:
              rootData.forceSoloTarif ??
              yData.clientData?.forceSoloTarif ??
              rootData.clientData?.forceSoloTarif,
          };

          return {
            uid,
            benchmarkYear: cleanYear,
            ...rootData,
            clientData: mergedCd,
          };
        } catch {
          return null;
        }
      })
    );

    const validDocs = hydratedResults.filter((item) => item !== null);
    cachedByYear[cleanYear] = { docs: validDocs, at: Date.now() };
    return validDocs;
  } catch (err) {
    console.warn('[SystemBenchmark] Konnte aktive Salons nicht laden:', err);
    return cached?.docs || [];
  }
};

export const aggregateActiveSalonsCohort = (
  activeClients: any[],
  ownExtracted: ExtractedSalonMetrics,
  cohortRange: { min: number; max: number },
  settings: any,
  benchmarkYear: string,
  allowCrossCountry = false
): {
  peerCount: number;
  marketCount: number;
  peerAutoUnlocked: boolean;
  peerAverages: Record<string, number | null>;
  marketAverages: Record<string, number | null>;
  comparedSalons: ExtractedSalonMetrics[];
  ownLand: BenchmarkLandCode;
  allowCrossCountry: boolean;
} => {
  const safeSettings = settings || defaultSettings;
  const targetMode = ownExtracted.mode;
  const ownLand = ownExtracted.land || 'DE';
  const sameModeMetrics: ExtractedSalonMetrics[] = [];

  (activeClients || []).forEach((entry) => {
    if (!isStrictlyActiveSalonRoot(entry)) return;
    if (entry.benchmarkYear && String(entry.benchmarkYear) !== String(benchmarkYear)) {
      return;
    }

    const rawCd = entry?.clientData
      ? { ...defaultClientData, ...entry, ...entry.clientData }
      : { ...defaultClientData, ...entry };

    const entryMode: BenchmarkMode = isSoloModeActive(rawCd) ? 'solo' : 'team';
    if (entryMode !== targetMode) return;

    // Standard: nur gleiches Land (DE↔DE, CH↔CH, …) — Cross-Country nur mit Opt-in
    if (!allowCrossCountry) {
      const entryLand = resolveSalonLand(rawCd, resolveSalonLand(entry));
      if (entryLand !== ownLand) return;
    }

    try {
      const { calc: c, clientData: convergedCd } =
        computeMetricsWithAutoConvergence(rawCd, safeSettings);
      const extracted = extractSalonBenchmarkMetrics(
        c,
        convergedCd,
        entry.uid || entry.id,
        benchmarkYear
      );
      if (!allowCrossCountry && extracted.land !== ownLand) return;
      if (extracted.monatsumsatzNetto > 0) {
        sameModeMetrics.push(extracted);
      }
    } catch {
      // Beschädigte Akte überspringen
    }
  });

  if (ownExtracted.monatsumsatzNetto > 0) {
    const idx = sameModeMetrics.findIndex(
      (m) =>
        (ownExtracted.mandantNr &&
          m.mandantNr &&
          m.mandantNr === ownExtracted.mandantNr) ||
        (ownExtracted.firmenname &&
          m.firmenname &&
          m.firmenname === ownExtracted.firmenname)
    );
    if (idx >= 0) {
      sameModeMetrics[idx] = ownExtracted;
    } else {
      sameModeMetrics.push(ownExtracted);
    }
  }

  const plausibleMarketSalons = sameModeMetrics.filter(
    (m) => m.isPlausibleForMarket
  );
  const effectiveMarketPool =
    plausibleMarketSalons.length > 0 ? plausibleMarketSalons : sameModeMetrics;

  const marketCount = effectiveMarketPool.length;

  const otherSalonsSameMode = effectiveMarketPool.filter(
    (m) =>
      !(
        ownExtracted.mandantNr &&
        m.mandantNr &&
        m.mandantNr === ownExtracted.mandantNr
      )
  );

  const peerPool =
    targetMode === 'solo'
      ? otherSalonsSameMode.length > 0
        ? otherSalonsSameMode
        : effectiveMarketPool
      : effectiveMarketPool.filter(
          (m) =>
            cohortRange.max > 0 &&
            m.monatsumsatzNetto >= cohortRange.min &&
            m.monatsumsatzNetto <= cohortRange.max
        );

  const peerCount = peerPool.length;
  const peerAutoUnlocked =
    targetMode === 'solo' ? peerCount > 0 : peerCount > TEAM_PEER_MIN_STRICT;

  const peerAverages: Record<string, number | null> = {};
  const marketAverages: Record<string, number | null> = {};

  ALL_METRIC_KEYS.forEach((k) => {
    const isSigned =
      k === 'bwaErgebnisProzent' ||
      k === 'rentabilitaetSoll' ||
      k === 'rentabilitaetIst' ||
      k === 'ueberschussSollProzent';
    const aggFn = isSigned ? avgAllowSigned : avg;

    peerAverages[k] =
      peerPool.length > 0 ? aggFn(peerPool.map((m) => n(m.values[k]))) : null;
    marketAverages[k] =
      effectiveMarketPool.length > 0
        ? aggFn(effectiveMarketPool.map((m) => n(m.values[k])))
        : null;
  });

  return {
    peerCount,
    marketCount,
    peerAutoUnlocked,
    peerAverages,
    marketAverages,
    comparedSalons: sameModeMetrics,
    ownLand,
    allowCrossCountry,
  };
};

export const buildBenchmarkMatrix = (
  calc: any,
  clientData: any,
  activeClientsPool: any[] = [],
  settings?: any,
  benchmarkYear?: string,
  allowCrossCountry = false
): {
  benchmarkYear: string;
  mode: BenchmarkMode;
  selectedCohortRange: { min: number; max: number };
  monatsumsatzNetto: number;
  pointsSoll: BenchmarkDataPoint[];
  pointsIst: BenchmarkDataPoint[];
  peerCount: number;
  marketCount: number;
  peerAutoUnlocked: boolean;
  comparedSalons: ExtractedSalonMetrics[];
  ownLand: BenchmarkLandCode;
  allowCrossCountry: boolean;
} => {
  const activeYear = benchmarkYear || resolveActiveBenchmarkYear(clientData);
  const { calc: convergedOwnCalc, clientData: convergedOwnCd } =
    computeMetricsWithAutoConvergence(clientData, settings ?? calc?.settings);
  const own = extractSalonBenchmarkMetrics(
    convergedOwnCalc,
    convergedOwnCd,
    undefined,
    activeYear
  );
  const { mode, monatsumsatzNetto, auslastungPct } = own;

  const selectedCohortRange = {
    min: monatsumsatzNetto * (1 - COHORT_PCT),
    max: monatsumsatzNetto * (1 + COHORT_PCT),
  };

  const cohort = aggregateActiveSalonsCohort(
    activeClientsPool,
    own,
    selectedCohortRange,
    settings ?? calc?.settings,
    activeYear,
    allowCrossCountry
  );

  const makePoint = (
    metricKey: string,
    label: string,
    unit: BenchmarkUnit,
    section: BenchmarkDataPoint['section'],
    sublabel?: string,
    targetHint?: string,
    secondaryMarketNote?: BenchmarkDataPoint['secondaryMarketNote'],
    options?: {
      targetHintTone?: BenchmarkDataPoint['targetHintTone'];
      rowAccentTone?: BenchmarkDynamicTone;
    }
  ): BenchmarkDataPoint => {
    const mySalonValue = n(own.values[metricKey]);
    const peerGroupValue = cohort.peerAverages[metricKey] ?? null;
    const totalMarketValue = cohort.marketAverages[metricKey] ?? null;
    const dyn = evaluateBySystemTarget(metricKey, mySalonValue, own.values, mode);

    return {
      metricKey,
      label,
      sublabel,
      targetHint,
      targetHintTone: options?.targetHintTone,
      unit,
      mySalonValue,
      peerGroupValue,
      totalMarketValue,
      secondaryMarketNote,
      status: dyn.status,
      statusLabel: dyn.statusLabel,
      statusTone: dyn.statusTone,
      statusMessage: dyn.statusMessage,
      rowAccentTone: options?.rowAccentTone,
      peerAvailable: cohort.peerAutoUnlocked,
      peerSampleSize: cohort.peerCount,
      marketSampleSize: cohort.marketCount,
      section,
    };
  };

  const ohneMatEmpf = Math.round(n(own.values.stundensatzOhneMatEmpfohlen));
  const ohneMatReell = Math.round(n(own.values.stundensatzOhneMatReell));
  const inklMatEmpf = Math.round(n(own.values.stundensatzInklMatEmpfohlen));
  const inklMatReell = Math.round(n(own.values.stundensatzInklMatReell));

  const stundensatzRows: BenchmarkDataPoint[] = [];

  // Stundensatz ohne Material:
  // Wenn Empfohlen == Übernommen (wie bei Susi: 80 € Empfohlen und 80 € Übernommen), NUR EINE Zeile anzeigen!
  if (ohneMatEmpf === ohneMatReell) {
    stundensatzRows.push(
      makePoint(
        'stundensatzOhneMatReell',
        'Stundensatz ohne Material (Empfohlen & Übernommen)',
        '€/Std.',
        'leistung',
        'Empfohlener Stundensatz wurde 1:1 für den Salon übernommen (Brutto)',
        undefined,
        {
          label: 'Empfohlen Ø',
          peerValue: cohort.peerAverages.stundensatzOhneMatEmpfohlen ?? null,
          marketValue: cohort.marketAverages.stundensatzOhneMatEmpfohlen ?? null,
        }
      )
    );
  } else {
    stundensatzRows.push(
      makePoint(
        'stundensatzOhneMatEmpfohlen',
        'Stundensatz ohne Material (Empfohlen)',
        '€/Std.',
        'leistung',
        'Kalkulator-Empfehlung (Barometer Brutto)'
      ),
      makePoint(
        'stundensatzOhneMatReell',
        'Stundensatz ohne Material (Übernommen / Reell)',
        '€/Std.',
        'leistung',
        'Vom Salon abweichend eingetragener Stundensatz ohne Material (Brutto)',
        `Empfehlung: ${ohneMatEmpf} €/Std.`
      )
    );
  }

  // Stundensatz inkl. Material:
  // Wenn Empfohlen == Übernommen (wie bei Susi: 130 € Empfohlen und 130 € Übernommen), NUR EINE Zeile anzeigen!
  if (inklMatEmpf === inklMatReell) {
    stundensatzRows.push(
      makePoint(
        'stundensatzInklMatReell',
        'Stundensatz inkl. Material (Empfohlen & Übernommen)',
        '€/Std.',
        'leistung',
        'Empfohlener Stundensatz inkl. 50g Material wurde 1:1 übernommen (Brutto)',
        undefined,
        {
          label: 'Empfohlen Ø',
          peerValue: cohort.peerAverages.stundensatzInklMatEmpfohlen ?? null,
          marketValue: cohort.marketAverages.stundensatzInklMatEmpfohlen ?? null,
        }
      )
    );
  } else {
    stundensatzRows.push(
      makePoint(
        'stundensatzInklMatEmpfohlen',
        'Stundensatz inkl. Material (Empfohlen)',
        '€/Std.',
        'leistung',
        'Kalkulator-Empfehlung inkl. 50g Material (Brutto)'
      ),
      makePoint(
        'stundensatzInklMatReell',
        'Stundensatz inkl. Material (Übernommen / Reell)',
        '€/Std.',
        'leistung',
        'Vom Salon abweichend eingetragener Stundensatz inkl. Material (Brutto)',
        `Empfehlung: ${inklMatEmpf} €/Std.`
      )
    );
  }

  const pointsSoll: BenchmarkDataPoint[] = [
    makePoint(
      'gemeinkostenfaktor',
      'Gemeinkostenquote',
      '%',
      'faktoren',
      mode === 'solo'
        ? 'Soloselbstständige liegen meist über 40 % — Ziel ist es, unter 50 % zu bleiben'
        : 'Gemeinkosten im Verhältnis zum Netto-Monatsumsatz — unbedingt unter 40 %, Ziel möglichst ~30 %',
      mode === 'solo'
        ? 'Solo-Ziel: < 50 %'
        : 'Unbedingt < 40 % · Ziel: ~30 %',
      undefined,
      { targetHintTone: 'cyan', rowAccentTone: 'cyan' }
    ),
    makePoint(
      'prodLohnkostenfaktor',
      'Produktive Lohnquote',
      '%',
      'faktoren',
      'Gesunder Korridor liegt bei 40–45 % — alles darunter oder darüber ist mit Vorsicht zu genießen',
      'Gesund: 40 – 45 %',
      undefined,
      { targetHintTone: 'indigo', rowAccentTone: 'indigo' }
    ),
    makePoint(
      'lohnfaktorAktiv',
      own.isManualLfHigher
        ? 'Lohnfaktor aktiv (Manuell erhöht)'
        : 'Lohnfaktor aktiv',
      'Faktor',
      'faktoren',
      'Abhängig vom Preisniveau — dein Stundensatz muss den notwendigen Soll-Stundenumsatz tragen',
      'Preisabhängig',
      undefined,
      { targetHintTone: 'gold', rowAccentTone: 'gold' }
    ),
    makePoint(
      'rentabilitaetSoll',
      'Kalkulatorische Rentabilität (Soll)',
      '%',
      'faktoren',
      'Inhaber-Ertrag im Verhältnis zum Netto-Monatsumsatz (< 30 % grenzwertig · < 15 % kritisch)',
      'Ziel: > 35 % (< 30 % grenzw. · < 15 % krit.)',
      undefined,
      { targetHintTone: 'purple' }
    ),
    makePoint(
      'materialkostenquote',
      'Materialeinsatz (BWA)',
      '%',
      'kostenquoten',
      'Materialeinsatz in % vom Netto-Umsatz (synchron zur BWA-Kostenstruktur)',
      '8–10% Planziel',
      undefined,
      { targetHintTone: 'orange', rowAccentTone: 'teal' }
    ),
    makePoint(
      'raumkostenquote',
      'Raumkosten (BWA)',
      '%',
      'kostenquoten',
      'Miete & Raumkosten in % vom Netto-Umsatz (alles unter 10 % ist exzellent)',
      '10–12% Planziel',
      undefined,
      { targetHintTone: 'orange', rowAccentTone: 'orange' }
    ),
    makePoint(
      'werbekostenquote',
      'Werbe-/Reisekosten (BWA)',
      '%',
      'kostenquoten',
      'Marketing & Reisekosten in % vom Netto-Umsatz (variable Betriebskosten)',
      '2–5% Planziel',
      undefined,
      { targetHintTone: 'orange', rowAccentTone: 'orange' }
    ),
    makePoint(
      'sonstigeKostenquote',
      'Sonstige Kosten (BWA)',
      '%',
      'kostenquoten',
      'Sonstige BWA-Betriebskosten in % vom Netto-Umsatz (variable Betriebskosten)',
      'ca. 10–12% Planziel',
      undefined,
      { targetHintTone: 'orange', rowAccentTone: 'orange' }
    ),
    makePoint(
      'grundlohnProdAvg',
      'Grundlohn produktiv Ø (rein vertraglich)',
      '€/Std.',
      'leistung',
      'Reines Grundgehalt ohne Lohnzusätze Ø produktive Mitarbeiter (Brutto)'
    ),
    makePoint(
      'gesamtlohnProdAvg',
      'Gesamtlohn produktiv Ø (inkl. Zusatz & Bonus)',
      '€/Std.',
      'leistung',
      'Grundgehalt + Lohnzusatz + Bonus Ø produktive Mitarbeiter (ohne AG-NK)'
    ),
    ...stundensatzRows,
    makePoint(
      'unternehmerlohn',
      'Kalkulatorischer Unternehmerlohn (inkl. Sozialkosten)',
      '€/Monat',
      'leistung',
      'Privatentnahme + Sozialkosten (echter Bedarf gesamt)'
    ),
    makePoint(
      'sollUmsatz100',
      'Soll-Umsatz / Std. bei 100 % Kapazität (Ø MA)',
      '€/Std.',
      'sollUmsatz',
      'Rechnerischer Mindest-Stundenumsatz bei 100 % Anwesenheit (Ø produktive MA)'
    ),
    makePoint(
      'sollUmsatz75',
      `Soll-Umsatz / Std. bei ${auslastungPct || 75} % Auslastung (Ø MA)`,
      '€/Std.',
      'sollUmsatz',
      'Notwendiger Brutto-Stundenumsatz der Mitarbeiter — muss durch den realen Stundensatz gedeckt sein',
      ohneMatReell > 0 ? `Dein Satz o.M.: ${ohneMatReell} €/Std.` : undefined
    ),
  ];

  const pointsIst: BenchmarkDataPoint[] = [
    makePoint(
      'rentabilitaetIst',
      'Aktive / Reale Rentabilität (Gesamtleistung)',
      '%',
      'ist',
      'Realer Inhaber-Ertrag im Verhältnis zum erwirtschafteten Netto-Umsatz (< 30 % grenzwertig · < 15 % kritisch)',
      'Ziel: > 35 % (< 30 % grenzw. · < 15 % krit.)',
      undefined,
      { targetHintTone: 'purple' }
    ),
    makePoint(
      'verkaufsfaktor',
      'Verkaufsfaktor (Gesamtleistung)',
      '%',
      'ist',
      'Verkaufsumsatz im Verhältnis zum Bedienumsatz (synchron zur Gesamtleistungsseite)',
      'Ziel: ≥ 10 % (ab 15 % Exzellent)',
      undefined,
      { targetHintTone: 'gold' }
    ),
    makePoint(
      'umsatzauslastung',
      'Ø Umsatzauslastung (Gesamtleistung)',
      '%',
      'ist',
      'Erreichte Umsatzauslastung im Verhältnis zum Soll-Stundenumsatz',
      'Ziel: 70 – 75 % (ab 85 % Exzellent)',
      undefined,
      { targetHintTone: 'gold' }
    ),
    makePoint(
      'bwaErgebnisProzent',
      'Laufender Überschuss / Planabweichung (Gesamtleistung)',
      '%',
      'ist',
      'Durchschnittlicher prozentualer Überschuss über Mindestumsatz (synchron zur Gesamtleistungsseite)',
      'Ziel: ≥ +10,0 %',
      undefined,
      { targetHintTone: 'gold' }
    ),
  ];

  return {
    benchmarkYear: activeYear,
    mode,
    selectedCohortRange,
    monatsumsatzNetto,
    pointsSoll,
    pointsIst,
    peerCount: cohort.peerCount,
    marketCount: cohort.marketCount,
    peerAutoUnlocked: cohort.peerAutoUnlocked,
    comparedSalons: cohort.comparedSalons,
    ownLand: cohort.ownLand,
    allowCrossCountry: cohort.allowCrossCountry,
  };
};

export interface SystemBenchmarkState {
  benchmarkYear: string;
  activeTab: BenchmarkTab;
  mode: BenchmarkMode;
  cohortMonths: number;
  selectedCohortRange: { min: number; max: number };
  monatsumsatzNetto: number;
  pointsSoll: BenchmarkDataPoint[];
  pointsIst: BenchmarkDataPoint[];
  peerCount: number;
  marketCount: number;
  peerAutoUnlocked: boolean;
  showSimilarColumn: boolean;
  /** Standard false: nur gleiches Land. Opt-in für DE/CH/AT/IT-Mischung. */
  allowCrossCountryComparison: boolean;
  ownLand: BenchmarkLandCode;
  isLoadingCohort: boolean;
  hydratedSalons: any[];
  comparedSalons: ExtractedSalonMetrics[];
  setActiveTab: (tab: BenchmarkTab) => void;
  setCohortMonths: (months: number) => void;
  setShowSimilarColumn: (show: boolean) => void;
  setAllowCrossCountryComparison: (allow: boolean) => void;
  loadActiveSalonsFromFirestore: (
    calc: any,
    clientData: any,
    settings?: any
  ) => Promise<void>;
  recomputeFromLive: (
    calc: any,
    clientData: any,
    activeClientsPool?: any[],
    settings?: any
  ) => void;
}

export const useSystemBenchmarkStore = create<SystemBenchmarkState>(
  (set, get) => ({
    benchmarkYear: resolveActiveBenchmarkYear(),
    activeTab: 'soll',
    mode: 'team',
    cohortMonths: 12,
    selectedCohortRange: { min: 0, max: 0 },
    monatsumsatzNetto: 0,
    pointsSoll: [],
    pointsIst: [],
    peerCount: 0,
    marketCount: 0,
    peerAutoUnlocked: false,
    showSimilarColumn: false,
    allowCrossCountryComparison: false,
    ownLand: 'DE',
    isLoadingCohort: false,
    hydratedSalons: [],
    comparedSalons: [],
    setActiveTab: (tab) => set({ activeTab: tab }),
    setCohortMonths: (months) =>
      set({ cohortMonths: Math.max(1, Math.min(24, Math.round(months))) }),
    setShowSimilarColumn: (show) =>
      set((state) => ({
        // Team: Spalte nur bei freigeschalteter Peer-Kohorte (n > 10)
        showSimilarColumn: show && (state.mode === 'solo' || state.peerAutoUnlocked),
      })),
    setAllowCrossCountryComparison: (allow) =>
      set({ allowCrossCountryComparison: Boolean(allow) }),
    loadActiveSalonsFromFirestore: async (calc, clientData, settings) => {
      const targetYear = resolveActiveBenchmarkYear(clientData);
      set({ isLoadingCohort: true, benchmarkYear: targetYear });
      const docs = await fetchHydratedActiveSalonsForYearReadOnly(targetYear);
      const built = buildBenchmarkMatrix(
        calc,
        clientData,
        docs,
        settings,
        targetYear,
        get().allowCrossCountryComparison
      );
      set({
        benchmarkYear: built.benchmarkYear,
        hydratedSalons: docs,
        isLoadingCohort: false,
        mode: built.mode,
        selectedCohortRange: built.selectedCohortRange,
        monatsumsatzNetto: built.monatsumsatzNetto,
        pointsSoll: built.pointsSoll,
        pointsIst: built.pointsIst,
        peerCount: built.peerCount,
        marketCount: built.marketCount,
        peerAutoUnlocked: built.peerAutoUnlocked,
        comparedSalons: built.comparedSalons,
        ownLand: built.ownLand,
        allowCrossCountryComparison: built.allowCrossCountry,
      });
    },
    recomputeFromLive: (calc, clientData, activeClientsPool, settings) => {
      const targetYear = resolveActiveBenchmarkYear(clientData);
      const pool =
        activeClientsPool && activeClientsPool.length > 0
          ? activeClientsPool
          : get().hydratedSalons;
      const built = buildBenchmarkMatrix(
        calc,
        clientData,
        pool,
        settings,
        targetYear,
        get().allowCrossCountryComparison
      );
      set({
        benchmarkYear: built.benchmarkYear,
        mode: built.mode,
        selectedCohortRange: built.selectedCohortRange,
        monatsumsatzNetto: built.monatsumsatzNetto,
        pointsSoll: built.pointsSoll,
        pointsIst: built.pointsIst,
        peerCount: built.peerCount,
        marketCount: built.marketCount,
        peerAutoUnlocked: built.peerAutoUnlocked,
        comparedSalons: built.comparedSalons,
        ownLand: built.ownLand,
        allowCrossCountryComparison: built.allowCrossCountry,
      });
    },
  })
);

export default useSystemBenchmarkStore;
